import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, X, Ship, Wrench, Store, Loader2 } from 'lucide-react';
import { createPortal } from 'react-dom';
import api from '../utils/api';
import { useDebounce } from '../hooks/useDebounce';

export interface GlobalSearchResult {
  id: number;
  type: 'boat' | 'motor' | 'dealer';
  title: string;
  subtitle: string;
  image?: string;
  price?: number;
  status?: string;
  url: string;
}

interface GlobalSearchProps {
  onClose?: () => void;
  onResultClick?: () => void;
  placeholder?: string;
  compact?: boolean; // compact mode for sidebar
}

export default function GlobalSearch({ onClose, onResultClick, placeholder = 'Cerca barche, motori, dealer...', compact = false }: GlobalSearchProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);
  
  // In compact mode, measure input position for fixed overlay
  useEffect(() => {
    if (!isOpen || !compact || !inputRef.current) {
      setDropdownRect(null);
      return;
    }
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownRect({ top: rect.bottom + 8, left: rect.left, width: Math.max(rect.width, 480) });

    const handleScroll = () => {
      if (inputRef.current) {
        const r = inputRef.current.getBoundingClientRect();
        setDropdownRect({ top: r.bottom + 8, left: r.left, width: Math.max(r.width, 480) });
      }
    };
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, compact]);
  
  // Perform search
  useEffect(() => {
    if (!debouncedQuery.trim() || debouncedQuery.length < 2) {
      setResults([]);
      return;
    }
    
    const search = async () => {
      setIsLoading(true);
      try {
        const [boatsRes, motorsRes, dealersRes] = await Promise.all([
          api.get('/boats', { params: { search: debouncedQuery, limit: 5 } }),
          api.get('/motors', { params: { search: debouncedQuery, limit: 5 } }),
          api.get('/dealers', { params: { search: debouncedQuery, limit: 5 } }),
        ]);
        
        const boats: GlobalSearchResult[] = (boatsRes?.data?.data?.boats || boatsRes?.data?.boats || []).map((b: any) => ({
          id: b.id,
          type: 'boat' as const,
          title: b.title || `${b.brand} ${b.model}`,
          subtitle: b.dealer_name || 'Dealer',
          image: b.images?.[0],
          price: b.price,
          status: b.status,
          url: `/boats/${b.id}`,
        }));
        
        const motors: GlobalSearchResult[] = (motorsRes?.data?.data?.motors || motorsRes?.data?.motors || []).map((m: any) => ({
          id: m.id,
          type: 'motor' as const,
          title: m.title || `${m.brand} ${m.model}`,
          subtitle: m.dealer_name || 'Dealer',
          image: m.images?.[0],
          price: m.price,
          status: m.status,
          url: `/motors/${m.id}`,
        }));
        
        const dealers: GlobalSearchResult[] = (dealersRes?.data?.data?.dealers || dealersRes?.data?.dealers || []).map((d: any) => ({
          id: d.id,
          type: 'dealer' as const,
          title: d.company_name || d.name,
          subtitle: d.city || d.location || '',
          url: `/dealers/${d.id}`,
        }));
        
        setResults([...boats, ...motors, ...dealers].slice(0, 12));
      } catch (error) {
        console.error('[GlobalSearch] Error:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    search();
  }, [debouncedQuery]);
  
  // Handle result click
  const handleSelect = (result: GlobalSearchResult) => {
    navigate(result.url);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    onClose?.();
    onResultClick?.();
  };
  
  // Keyboard navigation
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        handleSelect(results[selectedIndex]);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, results]);
  
  // Click outside to close (includes portal dropdown)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isInsideInput = containerRef.current?.contains(target);
      const isInsideDropdown = dropdownRef.current?.contains(target);
      if (!isInsideInput && !isInsideDropdown) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const getIcon = (type: GlobalSearchResult['type']) => {
    switch (type) {
      case 'boat': return <Ship className="w-5 h-5 text-blue-600" />;
      case 'motor': return <Wrench className="w-5 h-5 text-green-600" />;
      case 'dealer': return <Store className="w-5 h-5 text-purple-600" />;
    }
  };
  
  return (
    <div ref={containerRef} className={`relative ${compact ? 'w-full' : 'w-full max-w-2xl'}`}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <Filter className="w-5 h-5 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setSelectedIndex(-1);
          }}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          placeholder={placeholder}
          className={`w-full h-11 pl-10 pr-10 bg-white border border-gray-200 rounded-lg text-[17px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm ${
            compact ? 'h-9 text-sm pl-9 pr-9' : ''
          }`}
          style={compact ? { minHeight: '36px' } : undefined}
        />
        {(query || isLoading) && (
          <button
            onClick={() => {
              setQuery('');
              setResults([]);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            ) : (
              <X className="w-4 h-4 text-gray-400" />
            )}
          </button>
        )}
      </div>
      
      {/* Results Dropdown - fixed overlay in compact mode, absolute otherwise */}
      {isOpen && query.length >= 2 && (() => {
        const dropdown = (
          <div
            ref={dropdownRef}
            className="rounded-lg shadow-xl border max-h-[70vh] overflow-y-auto"
            style={{ backgroundColor: 'var(--au-surface, #fff)', borderColor: 'var(--au-border, #e5e7eb)' }}
            style={compact && dropdownRect ? {
              position: 'fixed',
              top: dropdownRect.top,
              left: dropdownRect.left,
              width: dropdownRect.width,
              zIndex: 9999,
            } : {
              position: 'absolute' as const,
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 50,
              marginTop: '0.5rem',
            }}
          >
            {results.length === 0 && !isLoading ? (
              <div className="p-8 text-center" style={{ color: 'var(--au-text-2, #6b7280)' }}>
                <Filter className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--au-text-3, #9ca3af)' }} />
                <p>Nessun risultato per "{query}"</p>
                <p className="text-sm mt-1">Prova con termini diversi</p>
              </div>
            ) : (
              <div className="py-2">
                {/* Group by type */}
                {(['boat', 'motor', 'dealer'] as const).map(type => {
                  const typeResults = results.filter(r => r.type === type);
                  if (typeResults.length === 0) return null;
                  
                  return (
                    <div key={type}>
                      <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider"
                           style={{ color: 'var(--au-text-3, #9ca3af)', backgroundColor: 'var(--au-surface, #f9fafb)' }}>
                        {type === 'boat' ? 'Barche' : type === 'motor' ? 'Motori' : 'Dealer'}
                      </div>
                      {typeResults.map((result) => (
                        <button
                          key={`${result.type}-${result.id}`}
                          onClick={() => handleSelect(result)}
                          className={`w-full px-4 py-3 flex items-center gap-3 transition-colors border-b last:border-0 ${
                            selectedIndex === results.indexOf(result) ? '' : ''
                          }`}
                          style={{
                            borderBottomColor: 'var(--au-border, #e5e7eb)',
                            backgroundColor: selectedIndex === results.indexOf(result) ? 'rgba(34,211,238,0.10)' : 'transparent',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(34,211,238,0.08)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = selectedIndex === results.indexOf(result) ? 'rgba(34,211,238,0.10)' : 'transparent'; }}
                        >
                          {/* Thumbnail */}
                          <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                               style={{ backgroundColor: 'var(--au-surface-2, #f3f4f6)' }}>
                            {result.image ? (
                              <img src={result.image} alt="" width={48} height={48} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                            ) : (
                              getIcon(result.type)
                            )}
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 text-left min-w-0">
                            <div className="font-medium truncate" style={{ color: 'var(--au-text, #111827)' }}>{result.title}</div>
                            <div className="text-sm truncate" style={{ color: 'var(--au-text-2, #6b7280)' }}>{result.subtitle}</div>
                            {result.price && (
                              <div className="text-sm font-semibold" style={{ color: 'var(--au-green, #059669)' }}>
                                €{result.price.toLocaleString('it-IT')}
                              </div>
                            )}
                          </div>
                          
                          {/* Status badge for boats/motors */}
                          {result.status && (
                            <span className="px-2 py-1 text-xs font-medium" style={{
                              borderRadius: '6px',
                              backgroundColor: result.status === 'available' ? 'rgba(52,211,153,0.18)' :
                                               result.status === 'reserved' ? 'rgba(251,191,36,0.18)' : 'rgba(251,113,133,0.18)',
                              color: result.status === 'available' ? 'var(--au-green, #059669)' :
                                     result.status === 'reserved' ? '#fbbf24' : 'var(--au-red, #f43f5e)',
                            }}>
                              {result.status === 'available' ? 'Disponibile' :
                               result.status === 'reserved' ? 'Prenotato' : 'Venduto'}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Footer */}
            {results.length > 0 && (
              <div className="px-4 py-2 border-t text-xs text-center"
                   style={{ borderColor: 'var(--au-border, #e5e7eb)', color: 'var(--au-text-3, #9ca3af)' }}>
                Premi ↑↓ per navigare, Enter per selezionare
              </div>
            )}
          </div>
        );

        // In compact mode, render via portal to escape sidebar overflow:hidden
        if (compact) {
          return createPortal(dropdown, document.body);
        }
        return dropdown;
      })()}
    </div>
  );
}
